import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import config from '../../config';
import './ExploreComingSoon.css';

const ExploreComingSoon = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useSelector((state) => state.user || {});

  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [message, setMessage] = useState('');

  const joinNow = async () => {
    if (status === 'submitting' || status === 'success') return;
    setStatus('submitting');
    setMessage('');
    try {
      const { data } = await axios.post(
        `${config.apiUrl}/api/v1/waitlist/me`,
        { source: 'explore' },
        { withCredentials: true }
      );
      setStatus('success');
      setMessage(data?.message || "You're on the waitlist.");
    } catch (err) {
      setStatus('error');
      setMessage(err?.response?.data?.message || 'Something went wrong. Try again.');
    }
  };

  // On mount: if logged in, check waitlist status so UI persists across reloads.
  // Also handles ?join=1 auto-join after returning from login.
  useEffect(() => {
    if (loading) return;
    const params = new URLSearchParams(location.search);
    const shouldJoin = params.get('join') === '1';

    if (shouldJoin) {
      params.delete('join');
      const cleaned = params.toString();
      navigate(
        `${location.pathname}${cleaned ? `?${cleaned}` : ''}`,
        { replace: true }
      );
    }

    if (!isAuthenticated) return;

    (async () => {
      try {
        const { data } = await axios.get(
          `${config.apiUrl}/api/v1/waitlist/me?source=explore`,
          { withCredentials: true }
        );
        if (data?.joined) {
          setStatus('success');
          setMessage("You're on the waitlist.");
          return;
        }
        if (shouldJoin) joinNow();
      } catch (_) {
        if (shouldJoin) joinNow();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isAuthenticated]);

  const handleClick = () => {
    if (isAuthenticated) {
      joinNow();
    } else {
      const dest = `${location.pathname}?join=1`;
      navigate(`/login?next=${encodeURIComponent(dest)}`);
    }
  };

  const buttonLabel =
    status === 'submitting' ? 'Joining…'
    : status === 'success'  ? 'Joined'
    : isAuthenticated       ? 'Join waitlist'
    :                         'Sign in to join waitlist';

  return (
    <>
      <Helmet>
        <title>Explore Waitlist | dmyor</title>
        <meta name="description" content="Join the waitlist for the dmyor Explore feed: a public archive of intentions and the proof of how people acted on them." />
        <meta property="og:image" content="https://dmyor.com/images/og-image.png" />
      </Helmet>

      <div className="ecs-page">
        <div className="ecs-inner">
          <div className="ecs-badge">Waitlist</div>

          <h1 className="ecs-title">
            The world's intent<br />
            <span className="ecs-accent">archive, opening soon.</span>
          </h1>

          <p className="ecs-body">
            Every vision and dream people choose to make public will live here: what they set out to do and the work behind it. A permanent intent archive, on the record for the world, kept forever. Join the waitlist and be first in when we open the feed.
          </p>

          <button
            type="button"
            className="ecs-cta"
            onClick={handleClick}
            disabled={status === 'submitting' || status === 'success'}
          >
            {buttonLabel}
          </button>

          {message && (
            <p className={`ecs-msg ${status === 'success' ? 'ecs-msg-ok' : status === 'error' ? 'ecs-msg-err' : ''}`}>
              {message}
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default ExploreComingSoon;
